/**
 * Agent UI actions — applies the ordered `ui_actions` returned by the
 * `/query/agent` endpoint to the SafeCurves app, and snapshots current UI
 * state to send with each query.
 *
 * All the reactive plumbing already exists in Map.svelte's `$effect`s
 * (filters -> setFilter, legend -> visibility, geojson -> setData), so most
 * actions are plain store mutations. The highlight overlay is the exception:
 * it's an ephemeral, agent-owned GeoJSON source/layer set added imperatively
 * to the map (same idempotent pattern as `addSatelliteLayer`), deliberately
 * outside the legend/filter machinery.
 */

import { goto } from '$app/navigation';
import { resolve } from '$app/paths';
import { page } from '$app/state';
import { getLogger } from '@logtape/logtape';
import type { ExpressionSpecification, GeoJSONSource, LngLatBoundsLike } from 'maplibre-gl';
import { appStore } from '#lib/stores/appStore.svelte.js';
import { mapInstanceStore } from '#lib/stores/mapInstanceStore.svelte.js';
import { agentFilterStore } from '#lib/stores/agentFilterStore.svelte.js';
import { legendStore } from '#lib/stores/legendStore.svelte.js';
import { tableViewStore } from '#lib/stores/tableViewStore.svelte.js';
import { parseUiAction } from './aiApiClient';
import type { UiAction } from './aiApiClient';

const log = getLogger(['safesystem', 'safecurves', 'agent-ui-actions']);

const HIGHLIGHT_SOURCE_ID = 'agent-highlight-source';
const HIGHLIGHT_COLOR = '#00b8d9';

const LOCATOR_SOURCE_ID = 'agent-locator-source';
// Beacons hand over to native symbology as the user zooms in.
const LOCATOR_FADE_OUT_ZOOM = 13;

const EMPTY_FC = { type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection;

// Locator-beacon positions per map layer — zoom-independent markers so sparse
// query results stay findable at wide zooms, where the app's own layers are
// zoom-gated and may render few or none of the matches.
const locatorsByLayer: Record<string, [number, number][]> = {};

// Feature id -> representative point, accumulated across everything the agent
// has put on the canvas. Backs click-to-fly from links in its answers
// (`[Corridor 4821](#feature-4821)`).
const featurePoints = new Map<string, [number, number]>();

const FOCUS_SOURCE_ID = 'agent-focus-source';

const MODULE_TABLE_TAB: Record<string, string> = {
	identify: 'Curves',
	prioritize: 'Corridors',
	interventions: 'Signage'
};

function currentView(): string {
	try {
		return (page.params?.view as string) ?? 'dashboard';
	} catch {
		return 'dashboard';
	}
}

function ensureHighlightLayers(map: NonNullable<typeof mapInstanceStore.map>) {
	if (map.getSource(HIGHLIGHT_SOURCE_ID)) return;
	map.addSource(HIGHLIGHT_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
	map.addLayer({
		id: 'agent-highlight-fill',
		type: 'fill',
		source: HIGHLIGHT_SOURCE_ID,
		filter: ['==', ['geometry-type'], 'Polygon'],
		paint: { 'fill-color': HIGHLIGHT_COLOR, 'fill-opacity': 0.25 }
	});
	map.addLayer({
		id: 'agent-highlight-line',
		type: 'line',
		source: HIGHLIGHT_SOURCE_ID,
		filter: ['!=', ['geometry-type'], 'Point'],
		paint: {
			'line-color': HIGHLIGHT_COLOR,
			'line-width': 5,
			'line-opacity': 0.85
		}
	});
	map.addLayer({
		id: 'agent-highlight-point',
		type: 'circle',
		source: HIGHLIGHT_SOURCE_ID,
		filter: ['==', ['geometry-type'], 'Point'],
		paint: {
			'circle-color': HIGHLIGHT_COLOR,
			'circle-radius': 7,
			'circle-opacity': 0.9,
			'circle-stroke-color': '#ffffff',
			'circle-stroke-width': 2
		}
	});
}

function ensureLocatorLayers(map: NonNullable<typeof mapInstanceStore.map>) {
	if (map.getSource(LOCATOR_SOURCE_ID)) return;
	map.addSource(LOCATOR_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
	const fadeOpacity = (peak: number) =>
		[
			'interpolate',
			['linear'],
			['zoom'],
			4,
			peak,
			11,
			peak * 0.8,
			LOCATOR_FADE_OUT_ZOOM,
			0
		] as unknown as number;
	map.addLayer({
		id: 'agent-locator-halo',
		type: 'circle',
		source: LOCATOR_SOURCE_ID,
		maxzoom: LOCATOR_FADE_OUT_ZOOM,
		paint: {
			'circle-color': HIGHLIGHT_COLOR,
			'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 12, 12, 20] as unknown as number,
			'circle-opacity': fadeOpacity(0.2)
		}
	});
	map.addLayer({
		id: 'agent-locator-dot',
		type: 'circle',
		source: LOCATOR_SOURCE_ID,
		maxzoom: LOCATOR_FADE_OUT_ZOOM,
		paint: {
			'circle-color': HIGHLIGHT_COLOR,
			'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 4, 12, 6] as unknown as number,
			'circle-opacity': fadeOpacity(0.9),
			'circle-stroke-color': '#ffffff',
			'circle-stroke-width': 1.5,
			'circle-stroke-opacity': fadeOpacity(0.9)
		}
	});
}

function syncLocatorSource() {
	const map = mapInstanceStore.map;
	if (!map) return;
	ensureLocatorLayers(map);
	const features = Object.values(locatorsByLayer)
		.flat()
		.map((coordinates) => ({
			type: 'Feature' as const,
			properties: {},
			geometry: { type: 'Point' as const, coordinates }
		}));
	(map.getSource(LOCATOR_SOURCE_ID) as GeoJSONSource).setData({
		type: 'FeatureCollection',
		features
	});
}

function clearLocators() {
	for (const key of Object.keys(locatorsByLayer)) delete locatorsByLayer[key];
	setFocusPoint(null);
	syncLocatorSource();
}

/**
 * Forget the id -> coordinate lookup behind click-to-fly. Tied to the
 * transcript, not the map: closing the panel drops the overlays but leaves the
 * answers on screen, and their links have to keep working when it reopens.
 */
export function resetFeaturePoints() {
	featurePoints.clear();
}

/**
 * Ring marking the feature the user last clicked in an answer, so it's
 * identifiable among everything else on the map. Pass null to clear.
 */
function setFocusPoint(point: [number, number] | null) {
	const map = mapInstanceStore.map;
	if (!map) return;
	if (!map.getSource(FOCUS_SOURCE_ID)) {
		map.addSource(FOCUS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
		map.addLayer({
			id: 'agent-focus-ring',
			type: 'circle',
			source: FOCUS_SOURCE_ID,
			paint: {
				'circle-radius': 14,
				'circle-color': 'rgba(0,0,0,0)',
				'circle-stroke-color': HIGHLIGHT_COLOR,
				'circle-stroke-width': 3,
				'circle-stroke-opacity': 0.95
			}
		});
	}
	const data: GeoJSON.FeatureCollection = point
		? {
				type: 'FeatureCollection',
				features: [
					{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: point } }
				]
			}
		: EMPTY_FC;
	(map.getSource(FOCUS_SOURCE_ID) as GeoJSONSource).setData(data);
}

/**
 * Fly to a feature the agent referenced in an answer. Returns false when the
 * id isn't one of the features currently on the canvas (e.g. it fell outside
 * the point cap), so the caller can stay quiet rather than pretend.
 */
export function focusFeature(featureId: string): boolean {
	const point = featurePoints.get(featureId);
	const map = mapInstanceStore.map;
	if (!point || !map) return false;
	map.flyTo({ center: point, zoom: Math.max(map.getZoom(), 14), duration: 1200 });
	setFocusPoint(point);
	return true;
}

function setHighlightData(data: GeoJSON.FeatureCollection): boolean {
	const map = mapInstanceStore.map;
	if (!map) return false;
	ensureHighlightLayers(map);
	(map.getSource(HIGHLIGHT_SOURCE_ID) as GeoJSONSource).setData(data);
	return true;
}

// Measured off the live element so this tracks the panel's own CSS width and
// reads 0 while it is closed.
function chatPanelWidth(): number {
	if (typeof document === 'undefined') return 0;
	const panel = document.querySelector('[data-testid="data-chat-panel"]');
	return panel ? panel.getBoundingClientRect().width : 0;
}

function fitMapTo(bbox: number[]): boolean {
	const map = mapInstanceStore.map;
	if (!map || bbox.length !== 4) return false;
	map.fitBounds(bbox as LngLatBoundsLike, {
		padding: { top: 80, bottom: 80, right: 80, left: 80 + chatPanelWidth() },
		duration: 1200,
		maxZoom: 14
	});
	return true;
}

// Narrower than the `[[view=view]]` route matcher on purpose: the assistant
// answers through the map and its own tables, and sending the user to the
// table view only splits where the answer lives. Anything outside this list
// is refused rather than navigated, which also stops an unexpected value
// 404ing the shell.
const VIEWS = ['dashboard', 'map'] as const;

function applySwitchView(view: string): string {
	if (!(VIEWS as readonly string[]).includes(view)) {
		log.warn('switch_view rejected unknown view: {view}', { view });
		return `can't open '${view}' - the views are ${VIEWS.join(', ')}`;
	}
	const base = (appStore.app?.basePath ?? '/safecurves').slice(1);
	void goto(resolve(view === 'dashboard' ? base : `${base}/${view}`));
	return `opened the ${view} view`;
}

function applySwitchModule(module: string): string {
	const tab = MODULE_TABLE_TAB[module];
	if (!tab) return `unknown module '${module}'`;
	if (currentView() === 'table') tableViewStore.activeTab = tab;

	// Actions only apply while the panel is open (AI display mode), where the map is driven by
	// show_only_features and the user's legend must stay untouched for restore-on-exit.
	return `noted — the ${module} module's data shows when you show its layers`;
}

/**
 * Map layer ids embed the source WFS feature-type name:
 * `${feature.id}|${layerId}` (e.g. 'SC_v12:safecurves_prioritize_state-…|Prioritize').
 *
 * One WFS layer can drive SEVERAL map layers — the accidents feature renders
 * as both a heatmap (low zoom) and circles (high zoom) — so this returns all
 * of them. Filtering only the first would leave the others with no agent
 * entry, and in AI display mode that means hidden.
 */
function resolveMapLayers(wfsLayerName: string) {
	return legendStore.layers.filter((layer) => layer.layerId.split('|')[0] === wfsLayerName);
}

type ShowOnlyAction = Extract<UiAction, { type: 'show_only_features' }>;
type HighlightAction = Extract<UiAction, { type: 'highlight_features' }>;

function applyShowOnly(action: ShowOnlyAction): string {
	const wfsLayerName = action.layer_name;
	const ids = action.ids ?? [];
	const matchAll = action.match_all === true;
	if (!matchAll && ids.length === 0) return 'no matching features to show';

	const mapLayers = resolveMapLayers(wfsLayerName);
	if (mapLayers.length === 0) {
		log.warn('show_only_features: no map layer for {wfsLayerName}', { wfsLayerName });
		return `couldn't find a map layer for ${wfsLayerName}`;
	}

	// In AI display mode a store entry makes the layer visible; the expression
	// narrows it (null = whole layer). `match` is fast at thousands of ids.
	const expression = matchAll
		? null
		: (['match', ['get', 'id'], ids, true, false] as unknown as ExpressionSpecification);
	const label = action.label ?? null;
	for (const mapLayer of mapLayers) {
		agentFilterStore.setFilter(mapLayer.layerId, expression, label);
	}

	// Locator beacons keep sparse results findable at wide zooms; whole-layer
	// shows come without locations (data is everywhere). Keyed by WFS name so
	// multi-layer features don't draw duplicate beacons.
	const points = action.feature_points ?? {};
	const locations = Object.values(points);
	if (locations.length > 0) {
		locatorsByLayer[wfsLayerName] = locations;
	} else {
		delete locatorsByLayer[wfsLayerName];
	}
	for (const [featureId, point] of Object.entries(points)) {
		featurePoints.set(featureId, point);
	}
	syncLocatorSource();

	if (action.zoom !== false && action.bbox) fitMapTo(action.bbox);

	if (matchAll) return `showing ${label ?? `all of ${wfsLayerName}`} on the map`;
	const total = action.matched_total ?? ids.length;
	const subset = total > ids.length ? ` (first ${ids.length} of ${total})` : '';
	return `showing only ${label ?? `${ids.length} matching features`}${subset} on the map`;
}

function applyHighlight(action: HighlightAction): string {
	const geojson = action.geojson;
	if (!geojson.features.length) return 'nothing to highlight';
	if (!setHighlightData(geojson)) return 'map not ready — highlight skipped';
	if (action.zoom !== false && action.bbox) fitMapTo(action.bbox);
	const label = action.label ?? 'matching features';
	return `highlighted ${geojson.features.length} features: ${label}`;
}

function applyHideLayers(names: string[]): string {
	let removed = 0;
	for (const name of names) {
		const mapLayers = resolveMapLayers(name);
		for (const mapLayer of mapLayers) {
			agentFilterStore.removeFilter(mapLayer.layerId);
		}
		if (mapLayers.length > 0) {
			delete locatorsByLayer[name];
			removed += 1;
		}
	}
	syncLocatorSource();
	return removed > 0
		? `removed ${removed} layer${removed > 1 ? 's' : ''} from the map`
		: 'no matching layers to remove';
}

/** Apply one validated action; returns its transcript line, or null when unhandled. */
function applyAction(action: UiAction): string | null {
	switch (action.type) {
		case 'switch_view':
			return applySwitchView(action.view);
		case 'switch_module':
			return applySwitchModule(action.module);
		case 'set_map_filters':
			// The map is agent-driven while actions apply, so writing filters would silently change the user's.
			return 'noted - ask me to show the features you want instead while I control the map';
		case 'zoom_map':
			return fitMapTo(action.bbox) ? 'zoomed the map' : 'map not ready';
		case 'show_only_features':
			return applyShowOnly(action);
		case 'hide_layers':
			return applyHideLayers(action.layer_names);
		case 'clear_data_filter':
			agentFilterStore.clearAll();
			clearLocators();
			return 'cleared the map canvas';
		case 'highlight_features':
			return applyHighlight(action);
		case 'clear_highlights':
			setHighlightData(EMPTY_FC);
			return 'cleared highlights';
		default: {
			const exhaustive: never = action;
			log.warn('unhandled agent ui action {action}', { action: exhaustive });
			return null;
		}
	}
}

/**
 * Apply agent actions in order. Returns one human-readable line per action
 * for the chat transcript.
 */
export function applyUiActions(rawActions: unknown[]): string[] {
	const lines: string[] = [];
	for (const raw of rawActions) {
		const action = parseUiAction(raw);
		if (!action) {
			const type = (raw as { type?: unknown } | null)?.type;
			log.warn('agent ui action rejected {type}', { type });
			lines.push(`unsupported action '${String(type ?? 'unknown')}'`);
			continue;
		}
		try {
			const line = applyAction(action);
			if (line !== null) lines.push(line);
		} catch (error) {
			log.error('agent ui action failed {type} {error}', { type: action.type, error });
			lines.push(`action '${action.type}' failed`);
		}
	}
	return lines;
}

/** Remove agent overlays and data filters (new chat / reset). */
export function clearAgentHighlights() {
	setHighlightData(EMPTY_FC);
	agentFilterStore.clearAll();
	clearLocators();
}

/**
 * Enter AI display mode. The canvas is seeded with whatever layers are
 * currently visible (shown whole, in native symbology) so the map doesn't
 * blank out on open — the agent adjusts from there.
 */
export function enterAiDisplayMode() {
	agentFilterStore.setAiMode(true);
	for (const layer of legendStore.layers) {
		if (layer.layerAnyEnabled) {
			agentFilterStore.setFilter(layer.layerId, null, layer.systemId);
		}
	}
}

/** Exit AI display mode: restore the user's manual view and drop overlays. */
export function exitAiDisplayMode() {
	agentFilterStore.setAiMode(false);
	setHighlightData(EMPTY_FC);
	clearLocators();
}

/**
 * Snapshot the UI so the agent understands "here" / "what am I looking at"
 * and can skip redundant actions.
 */
export function collectUiState(): Record<string, unknown> {
	const state: Record<string, unknown> = { view: currentView() };

	state.ai_display_mode = agentFilterStore.aiMode;
	if (agentFilterStore.aiMode) {
		// What the agent currently has on the canvas, keyed by WFS layer name.
		// The user's manual legend/filter state is suspended in this mode, so
		// it is deliberately omitted.
		// Deduped by WFS layer name — several map layers can render one WFS
		// layer, and the agent reasons in WFS names.
		const shown = new Map<string, Record<string, unknown>>();
		for (const [layerId, entry] of Object.entries(agentFilterStore.layerFilters)) {
			const wfsName = layerId.split('|')[0];
			if (!shown.has(wfsName)) {
				shown.set(wfsName, {
					layer: wfsName,
					label: entry.label,
					whole_layer: entry.expression === null
				});
			}
		}
		state.shown_layers = [...shown.values()];
	}

	const map = mapInstanceStore.map;
	if (map) {
		try {
			const bounds = map.getBounds();
			const round = (value: number) => Math.round(value * 1e6) / 1e6;
			const west = round(bounds.getWest());
			const south = round(bounds.getSouth());
			const east = round(bounds.getEast());
			const north = round(bounds.getNorth());
			// Self-describing (a bare [a,b,c,d] invites coordinate-order bugs)
			// plus a drop-in ECQL string for "features in the user's view", so
			// "here"/"this area" questions can't be mis-scoped.
			state.viewport = {
				west,
				south,
				east,
				north,
				zoom: Math.round(map.getZoom() * 10) / 10,
				cql_filter: `BBOX(geom, ${west}, ${south}, ${east}, ${north}, 'EPSG:4326')`
			};
		} catch {
			// Camera not ready — omit.
		}
	}
	return state;
}
